import type { JSX, ReactNode } from 'react';
import { cn } from '@lib/cn';
import { useReveal } from '@lib/useReveal';

interface RevealOnViewProps {
  readonly children: ReactNode;
  /** Delay em ms antes da transicao comecar. Usado pra escalonar reveals em listas (ex: 0, 100, 200). Default: 0. */
  readonly delayMs?: number;
  /** Direcao do slide inicial. Default: 'up' (elemento sobe 16px). 'none' = so fade. */
  readonly slide?: 'up' | 'down' | 'none';
  readonly className?: string;
}

/**
 * Wrapper de reveal CSS-only (consome `useReveal`).
 *
 * Renderiza children num `<div>` que comeca `opacity-0 translate-y-4` (ou
 * outra direcao via prop `slide`) e transita para `opacity-100 translate-y-0`
 * quando o elemento entra no viewport.
 *
 * Por que nao usar `framer-motion`: PRD §7.4 proibe libs de animacao no MVP.
 * Esse wrapper + `useReveal` somam ~600 bytes minificado e cobrem ~80% dos
 * casos de reveal em landing institucional. Para casos complexos (orchestra
 * de paths SVG, parallax 3D, etc), a Phase 6 pode revisitar.
 *
 * Wrapper sempre renderiza `<div>` — se o consumer precisa de outra tag
 * semantica (li, section), envolve o `<RevealOnView>` na tag. Esta restricao
 * mantem o tipo do ref simples (HTMLDivElement) sem polymorphic union.
 *
 * Acessibilidade:
 * - Conteudo sempre presente no DOM (visual reveal apenas).
 * - `motion-reduce:transition-none` desliga a transicao para usuarios com
 *   `prefers-reduced-motion: reduce` configurado no SO (Tailwind built-in).
 * - O delay tambem e zerado em motion-reduce via `motion-reduce:delay-0`.
 *
 * Uso tipico (stagger de 3 itens):
 * ```tsx
 * <RevealOnView delayMs={0}>...</RevealOnView>
 * <RevealOnView delayMs={120}>...</RevealOnView>
 * <RevealOnView delayMs={240}>...</RevealOnView>
 * ```
 */
export const RevealOnView = ({
  children,
  delayMs = 0,
  slide = 'up',
  className,
}: RevealOnViewProps): JSX.Element => {
  const { ref, isRevealed } = useReveal();

  const slideClassByDirection = {
    up: isRevealed ? 'translate-y-0' : 'translate-y-4',
    down: isRevealed ? 'translate-y-0' : '-translate-y-4',
    none: 'translate-y-0',
  } as const;

  return (
    <div
      ref={ref}
      style={delayMs > 0 ? { transitionDelay: `${String(delayMs)}ms` } : undefined}
      className={cn(
        'transition-all duration-700 ease-out motion-reduce:transition-none motion-reduce:delay-0',
        isRevealed ? 'opacity-100' : 'opacity-0',
        slideClassByDirection[slide],
        className,
      )}
    >
      {children}
    </div>
  );
};
